import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Node, mergeAttributes } from "@tiptap/core";
import { showToast, uploadBlogImage } from "../../Pages/Utils/utils";
import { normalizeLinkUrl } from "./blogUtils";
import "./blog-editor.css";

// <div class="callout"><p>..</p></div>
const Callout = Node.create({
    name: "callout",
    group: "block",
    content: "block+",
    defining: true,

    parseHTML() {
        return [{ tag: "div.callout" }];
    },

    renderHTML() {
        return ["div", mergeAttributes({ class: "callout" }), 0];
    },

    addCommands() {
        return {
            toggleCallout:
                () =>
                ({ commands, editor }) =>
                    editor.isActive(this.name) ? commands.lift(this.name) : commands.wrapIn(this.name),
        };
    },
});

const BlogEditor = ({ content, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const lastLoaded = useRef(null);

    const editor = useEditor({
        shouldRerenderOnTransaction: true,
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                code: false,
                codeBlock: false,
                strike: false,
                link: false,
            }),
            Link.configure({ openOnClick: false, autolink: false }),
            Image.configure({ inline: false }),
            Placeholder.configure({ placeholder: "Write the post here..." }),
            Callout,
        ],
        content: content || "",
        onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML(), ed.isEmpty),
    });

    // Load content when it arrives asynchronously (edit page)
    useEffect(() => {
        if (!editor || !content || lastLoaded.current === content) return;
        if (editor.getHTML() !== content) {
            editor.commands.setContent(content, { emitUpdate: false });
        }
        lastLoaded.current = content;
    }, [editor, content]);

    if (!editor) return null;

    const setLink = () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("Link URL (leave empty to remove the link)", prev);
        if (url === null) return;
        if (url.trim() === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: normalizeLinkUrl(url) }).run();
    };

    const onImagePicked = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append("file", file);
            const res = await uploadBlogImage(fd);
            const url = res?.data?.url;
            if (res?.status == 200 && url) {
                editor.chain().focus().setImage({ src: url, alt: file.name.replace(/\.[^.]+$/, "") }).run();
            } else {
                showToast(res?.message || "Image upload failed", "error");
            }
        } catch (error) {
            showToast(error?.message || "Image upload failed", "error");
        } finally {
            setUploading(false);
        }
    };

    const Btn = ({ label, title, active, onClick, disabled }) => (
        <button
            type="button"
            title={title || label}
            className={`blog-tb-btn ${active ? "active" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    return (
        <div className="blog-editor">
            <div className="blog-toolbar">
                <Btn label="H2" title="Heading 2" active={editor.isActive("heading", { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
                <Btn label="H3" title="Heading 3" active={editor.isActive("heading", { level: 3 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
                <span className="blog-tb-sep" />
                <Btn label={<b>B</b>} title="Bold" active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()} />
                <Btn label={<i>I</i>} title="Italic" active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()} />
                <Btn label="Link" title="Add / edit / remove link" active={editor.isActive("link")} onClick={setLink} />
                <span className="blog-tb-sep" />
                <Btn label="&bull; List" title="Bullet list" active={editor.isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()} />
                <Btn label="1. List" title="Numbered list" active={editor.isActive("orderedList")}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()} />
                <Btn label="Quote" title="Quote" active={editor.isActive("blockquote")}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()} />
                <Btn label="Callout" title="Callout box" active={editor.isActive("callout")}
                    onClick={() => editor.chain().focus().toggleCallout().run()} />
                <Btn label={uploading ? "Uploading..." : "Image"} title="Insert image" disabled={uploading}
                    onClick={() => fileRef.current && fileRef.current.click()} />
                <span className="blog-tb-sep" />
                <Btn label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} />
                <Btn label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} />
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={onImagePicked} />
            </div>
            <EditorContent editor={editor} className="blog-content" />
        </div>
    );
};

export default BlogEditor;
